import React, { useState, useMemo } from 'react';
import { ClubOverviewSection } from './ClubOverviewSection';
import { Typography } from '../../common/Typography';
import LeaderboardRow from '../../leaderboard/LeaderboardRow';
import LeaderboardOverviewPreviewSkeleton from '../../leaderboard/LeaderboardOverviewPreviewSkeleton';

const LADDER_TABS = [
  { id: 'mixed', label: 'Mixed' },
  { id: 'levelsMale', label: "Men's" },
  { id: 'levelsFemale', label: "Women's" },
  { id: 'combined', label: 'Combined' },
];

/** Maps the active tab id to the key in snapshotMovement. */
const MOVEMENT_KEY = {
  mixed: 'mixed',
  levelsMale: 'levelsMale',
  levelsFemale: 'levelsFemale',
  combined: 'combined',
};

/**
 * Condensed leaderboard preview for My Club overview.
 * Shows top 3 rows from the selected ladder with rank movement arrows and user highlight.
 * Uses leaderboard data already loaded by ClubPage (no extra fetch).
 *
 * @param {Object} leaderboard       - { mixed, levelsMale, levelsFemale, combined, userPositions }
 * @param {Object} snapshotMovement  - { mixed: Map, levelsMale: Map, levelsFemale: Map, combined: Map }
 * @param {Object} currentUser       - { id, gender }
 * @param {Function} onViewAll       - navigates to Boards tab
 */
export function ClubLeaderboardOverviewPreview({
  leaderboard,
  snapshotMovement,
  leaderboardLoading = false,
  currentUser,
  onViewAll,
}) {
  const userGender = currentUser?.gender?.toLowerCase();
  const defaultTab = useMemo(() => {
    if (userGender === 'female') return 'levelsFemale';
    return 'mixed';
  }, [userGender]);

  const [activeTab, setActiveTab] = useState(defaultTab);

  const rows = useMemo(() => {
    const list = leaderboard?.[activeTab] ?? [];
    return list.slice(0, 3);
  }, [leaderboard, activeTab]);

  const eloKey =
    activeTab === 'mixed'
      ? 'mixed_elo_rating'
      : activeTab === 'combined'
        ? 'combined_elo_rating'
        : 'levels_elo_rating';
  const movementMap = snapshotMovement?.[MOVEMENT_KEY[activeTab]] ?? new Map();

  if (leaderboardLoading) {
    return (
      <ClubOverviewSection
        label="Leaderboards"
        variant="leaderboards"
        onViewAll={onViewAll}
        onSectionClick={onViewAll}
      >
        <LeaderboardOverviewPreviewSkeleton />
      </ClubOverviewSection>
    );
  }

  if (
    !leaderboard ||
    ((!leaderboard.mixed || leaderboard.mixed.length === 0) &&
      (!leaderboard.levelsMale || leaderboard.levelsMale.length === 0) &&
      (!leaderboard.levelsFemale || leaderboard.levelsFemale.length === 0) &&
      (!leaderboard.combined || leaderboard.combined.length === 0))
  ) {
    return null;
  }

  return (
    <ClubOverviewSection
      label="Leaderboards"
      variant="leaderboards"
      onViewAll={onViewAll}
      onSectionClick={onViewAll}
    >
      <div className="flex gap-1.5 mb-3 flex-wrap" onClick={(e) => e.stopPropagation()}>
        {LADDER_TABS.map((tab) => {
          const hasData = (leaderboard?.[tab.id]?.length ?? 0) > 0;
          if (!hasData) return null;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                activeTab === tab.id
                  ? 'bg-primary text-white'
                  : 'bg-surface text-text-secondary hover:bg-border-light'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-2">
        {rows.length === 0 ? (
          <Typography variant="muted" className="text-center py-4">No data for this ladder.</Typography>
        ) : (
          rows.map((item, idx) => {
            const movement = movementMap.get(item.players?.id);
            const isCurrentUser = item.players?.id === currentUser?.id;
            const userPosition = leaderboard?.userPositions?.[activeTab];
            const rank = isCurrentUser && userPosition ? userPosition : idx + 1;
            return (
              <LeaderboardRow
                key={item.players?.id ?? idx}
                rank={rank}
                player={item.players}
                elo={item[eloKey]}
                isCurrentUser={isCurrentUser}
                rankDelta={movement?.rank_delta ?? null}
                highlight={isCurrentUser ? 'primary' : 'none'}
                rankTierMode="preview"
                variant="compact"
              />
            );
          })
        )}
      </div>
    </ClubOverviewSection>
  );
}
